import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  FileMinus2,
  FileText,
  History,
  ShieldCheck,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import type { PaginaDeCorrecciones } from '@/shared/api/cliente';
import type { CorreccionListada } from '@/shared/api/tipos';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { POR_PAGINA, Paginador } from '@/shared/ui/Paginador';

export function PaginaFacturasAnuladas() {
  const [soloPendientes, setSoloPendientes] = useState(true);
  const [pagina, setPagina] = useState(0);

  const correcciones = useQuery({
    queryKey: ['correcciones', soloPendientes, pagina],
    queryFn: () =>
      api.get<PaginaDeCorrecciones<CorreccionListada>>('/correcciones', {
        query: { soloPendientes, pagina, tamanio: POR_PAGINA },
      }),
  });

  function filtrar(valor: boolean) {
    setSoloPendientes(valor);
    setPagina(0);
  }

  const filas = correcciones.data?.contenido ?? [];
  const total = correcciones.data?.totalElementos ?? 0;

  return (
    <>
      <EncabezadoDePagina
        icono={Ban}
        titulo="Facturas anuladas"
        subtitulo="Qué se dio de baja, por qué, y si ya se corrigió"
      />

      <div className="mb-5 flex gap-6 border-b border-armazon-200">
        <Pestana
          activa={soloPendientes}
          alPulsar={() => filtrar(true)}
          icono={AlertTriangle}
          cuantas={correcciones.data?.pendientes}
          alerta
        >
          Faltan por corregir
        </Pestana>
        <Pestana
          activa={!soloPendientes}
          alPulsar={() => filtrar(false)}
          icono={History}
          cuantas={correcciones.data?.historial}
        >
          Historial completo
        </Pestana>
      </div>

      {soloPendientes && total > 0 && (
        <div className="mb-5 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
          <p className="text-justify text-xs uppercase leading-relaxed text-amber-800">
            <span className="font-semibold">
              Estas facturas se anularon y nadie emitió la corregida.
            </span>{' '}
            Mientras sigan así, esa deuda no existe a nombre del ciudadano. Se terminan desde
            Emisión de Cobros › Corrección manual, buscando al cliente y el mes.
          </p>
        </div>
      )}

      {!soloPendientes && (
        <div className="mb-5 flex gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4">
          <History className="mt-0.5 size-4 shrink-0 text-sky-600" aria-hidden />
          <p className="text-justify text-xs uppercase leading-relaxed text-sky-800">
            Aquí están todas las anulaciones, también las del sistema anterior. Las de la tasa
            mensual se corrigen desde esta pantalla; las de servicios especiales, cargos puntuales o
            títulos se corrigen en su propio módulo —por ejemplo, refacturando el ciclo en Control
            de pesaje— y aquí solo se ve si ya se hizo. Las del sistema anterior son historial:
            dicen con qué factura se reemplazaron, si la hubo.
          </p>
        </div>
      )}

      {correcciones.isPending ? (
        <Cargando titulo="Cargando las anulaciones" detalle="Buscando lo que quedó sin corregir" />
      ) : filas.length === 0 ? (
        <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
          {soloPendientes
            ? 'No hay ninguna anulación pendiente de corregir'
            : 'Todavía no se ha anulado ninguna factura'}
        </p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
          <div className="divide-y divide-armazon-100">
            {filas.map((c) => (
              <Fila key={c.id} correccion={c} />
            ))}
          </div>

          <Paginador
            pagina={pagina}
            porPagina={POR_PAGINA}
            total={total}
            quePagina="anulaciones"
            alCambiar={setPagina}
          />
        </div>
      )}
    </>
  );
}

function Pestana({
  activa,
  alPulsar,
  icono: Icono,
  cuantas,
  alerta = false,
  children,
}: {
  activa: boolean;
  alPulsar: () => void;
  icono: typeof AlertTriangle;
  cuantas: number | undefined;
  alerta?: boolean;
  children: React.ReactNode;
}) {
  const urgente = alerta && (cuantas ?? 0) > 0;

  return (
    <button
      type="button"
      onClick={alPulsar}
      aria-current={activa ? 'page' : undefined}
      className={`-mb-px flex items-center gap-2 border-b-2 px-1 pb-3 text-[13px] font-semibold uppercase tracking-wide transition-colors ${
        activa
          ? 'border-marca-600 text-marca-700'
          : 'border-transparent text-armazon-400 hover:border-armazon-300 hover:text-armazon-600'
      }`}
    >
      <Icono className={`size-4 shrink-0 ${urgente ? 'text-amber-600' : ''}`} aria-hidden />
      {children}
      {cuantas !== undefined && (
        <span
          className={`importe rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
            urgente
              ? 'bg-amber-100 text-amber-700'
              : activa
                ? 'bg-marca-50 text-marca-700'
                : 'bg-armazon-100 text-armazon-500'
          }`}
        >
          {cuantas.toLocaleString('es-EC')}
        </span>
      )}
    </button>
  );
}

/** De dónde salió la factura anulada, y dónde se corrige lo que no es de la emisión mensual. */
const ORIGENES: Record<string, { rotulo: string; donde: string }> = {
  EMISION: { rotulo: 'Tasa mensual', donde: 'Facturas anuladas' },
  GENERADORES: { rotulo: 'Servicios especiales', donde: 'Control de pesaje' },
  CARGOS: { rotulo: 'Cargo puntual', donde: 'Cargos puntuales' },
  TITULOS: { rotulo: 'Art. 8', donde: 'Títulos del Art. 8' },
  SIRE: { rotulo: 'Cartera anterior', donde: 'su módulo' },
  SISTEMA_ANTERIOR: { rotulo: 'Sistema anterior', donde: 'el sistema anterior' },
};

function Fila({ correccion: c }: { correccion: CorreccionListada }) {
  const aqui = c.origen === 'EMISION';
  const anterior = c.origen === 'SISTEMA_ANTERIOR';
  const donde = ORIGENES[c.origen]?.donde ?? 'su módulo';
  return (
    <div className="px-6 py-4 transition-colors hover:bg-armazon-50/60">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <span className="importe text-sm font-bold text-armazon-800">
              FACTURA {c.numeroFactura ?? String(c.facturaOriginalId).padStart(6, '0')}
            </span>
            <span className="rounded-md bg-armazon-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
              {ORIGENES[c.origen]?.rotulo ?? c.origen}
            </span>
            <span className="importe rounded-md bg-armazon-100 px-1.5 py-0.5 text-[10px] font-semibold text-armazon-600">
              {c.periodo}
            </span>

            {c.notaDeCredito && (
              <span className="importe inline-flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                <FileMinus2 className="size-2.5" aria-hidden />
                NC {c.notaDeCredito}
              </span>
            )}
            {anterior ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-armazon-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-armazon-600">
                <History className="size-2.5" aria-hidden />
                {c.numeroFacturaNueva
                  ? `Reemplazada por la ${c.numeroFacturaNueva}`
                  : 'Sin factura de reemplazo'}
              </span>
            ) : c.sinReemitir ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                <AlertTriangle className="size-2.5" aria-hidden />
                {aqui ? 'Falta corregir' : `Falta corregir en ${donde}`}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-md bg-marca-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-marca-700">
                <CheckCircle2 className="size-2.5" aria-hidden />
                {aqui
                  ? `Corregida con la ${c.numeroFacturaNueva ?? String(c.facturaNuevaId).padStart(6, '0')}`
                  : `Refacturada en ${donde}`}
              </span>
            )}
          </p>

          <p className="mt-1 truncate text-xs uppercase text-armazon-700">
            {c.clienteNombre ?? `Cliente ${c.clienteId} (ya no está en el catastro)`}
            {c.clienteIdentificacion && (
              <span className="importe ml-2 text-[11px] text-armazon-400">
                {c.clienteIdentificacion}
              </span>
            )}
          </p>
        </div>

        <span className="importe text-lg font-bold text-armazon-300 line-through">
          ${c.total.toFixed(2)}
        </span>
      </div>

      <div className="mt-3 rounded-lg border border-armazon-200 bg-armazon-50/60 p-3">
        <p className="text-[11px] font-semibold uppercase text-armazon-700">
          {c.motivoCodigo} · {c.motivoNombre}
        </p>
        {c.observacion && (
          <p className="mt-1 text-justify text-[11px] leading-relaxed text-armazon-600">
            {c.observacion}
          </p>
        )}

        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase text-armazon-500">
          <Dato icono={FileText} texto={`Pidió: ${c.solicitadoPor}`} />
          {c.aprobadoPor && <Dato icono={ShieldCheck} texto={`Autorizó: ${c.aprobadoPor}`} />}
          {c.respaldo && <Dato icono={FileText} texto={c.respaldo} />}
          {c.anuladaEl && <span>{formatearCorto(c.anuladaEl)}</span>}
        </div>
      </div>
    </div>
  );
}

function Dato({ icono: Icono, texto }: { icono: typeof FileText; texto: string }) {
  return (
    <span className="flex items-center gap-1">
      <Icono className="size-2.5 shrink-0" aria-hidden />
      {texto}
    </span>
  );
}

function formatearCorto(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
